import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Building2 } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { useBranding } from '@/contexts/BrandingContext';
import { useLanguage } from '@/i18n/LanguageContext';
import Captcha from '@/components/Captcha';
import LanguageSelector from '@/components/LanguageSelector';
import { analytics, trackSignupSuccess } from '@/lib/analytics';

function CompanyAdminLogin() {
  const navigate = useNavigate();
  const { branding } = useBranding();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const captchaRef = useRef(null);
  const formStartedRef = useRef(false);
  
  // Custom Google OAuth state
  const [customOAuthConfig, setCustomOAuthConfig] = useState(null);
  const [googleConfigLoading, setGoogleConfigLoading] = useState(true);
  const [googleLoading, setGoogleLoading] = useState(false);
  
  useEffect(() => {
    checkAuth();
    fetchGoogleOAuthConfig();
  }, []);
  
  const fetchGoogleOAuthConfig = async () => {
    try {
      const response = await axios.get(`${API}/auth/google-oauth-config`);
      if (response.data.enabled && response.data.client_id) {
        setCustomOAuthConfig(response.data);
      }
    } catch (error) {
      console.log('Google OAuth config fetch failed');
    } finally {
      setGoogleConfigLoading(false);
    }
  };
  
  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      if (response.data.role === 'admin') {
        navigate('/admin/dashboard');
      }
    } catch (error) {
      // Not authenticated
    }
  };
  
  const handleCaptchaVerify = (data) => {
    setCaptchaVerified(true);
    setError('');
  };
  
  const handleCaptchaError = (errorMsg) => {
    setCaptchaVerified(false);
    setError(errorMsg);
  };
  
  const handleGoogleLogin = async () => {
    // If config still loading, wait
    if (googleConfigLoading) {
      setError('Google Sign-In is initializing. Please try again in a moment.');
      return;
    }

    // Only use direct Google OAuth popup — no Emergent redirect fallback
    if (!customOAuthConfig?.client_id) {
      setError('Google Sign-In is not configured. Please sign in with email and password.');
      return;
    }

    // Use custom Google OAuth with popup flow
    setGoogleLoading(true);
    setError('');
      
      try {
        // Dynamically load Google Identity Services script
        if (!window.google?.accounts?.oauth2) {
          await new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'https://accounts.google.com/gsi/client';
            script.async = true;
            script.defer = true;
            script.onload = () => {
              // Wait for google object to be available
              setTimeout(resolve, 100);
            };
            script.onerror = reject;
            document.head.appendChild(script);
          });
        }
        
        // Use OAuth2 Token Client for popup flow (more reliable)
        const tokenClient = window.google.accounts.oauth2.initTokenClient({
          client_id: customOAuthConfig.client_id,
          scope: 'email profile openid',
          callback: async (tokenResponse) => {
            if (tokenResponse.access_token) {
              try {
                // Get user info from Google
                const userInfoResponse = await axios.get('https://www.googleapis.com/oauth2/v3/userinfo', {
                  headers: { Authorization: `Bearer ${tokenResponse.access_token}` }
                });
                
                // Send to our backend
                const backendResponse = await axios.post(`${API}/auth/google-oauth-callback`, {
                  credential: tokenResponse.access_token,
                  user_info: userInfoResponse.data
                }, { withCredentials: true });
                
                handleOAuthSuccess(backendResponse.data);
              } catch (err) {
                console.error('OAuth callback error:', err);
                setError(err.response?.data?.detail || 'Failed to authenticate with Google');
                setGoogleLoading(false);
              }
            } else {
              setError('Google sign-in was cancelled');
              setGoogleLoading(false);
            }
          },
          error_callback: (error) => {
            console.error('Google OAuth error:', error);
            setError('Google sign-in failed. Please try again.');
            setGoogleLoading(false);
          }
        });
        
        // Request access token (opens popup)
        tokenClient.requestAccessToken();
        
      } catch (err) {
        console.error('Google OAuth error:', err);
        setError('Failed to initialize Google Sign-In');
        setGoogleLoading(false);
      }
  };
  
  const handleGoogleCredentialResponse = async (response) => {
    try {
      // Send the credential to our backend
      const backendResponse = await axios.post(`${API}/auth/google-oauth-callback`, {
        credential: response.credential
      }, { withCredentials: true });
      
      handleOAuthSuccess(backendResponse.data);
    } catch (err) {
      console.error('OAuth callback error:', err);
      setError(err.response?.data?.detail || 'Failed to sign in with Google');
      setGoogleLoading(false);
    }
  };
  
  const handleOAuthSuccess = async (data) => {
    console.log('OAuth success response:', data);
    if (data.success) {
      // Distinct paths — never reuse login_success for new-account conversion
      if (data.is_new_user === true) {
        trackSignupSuccess('google');
      } else {
        analytics.loginSuccess('admin_google');
      }
      // Set cookie with proper attributes for persistent sessions
      if (data.session_token) {
        document.cookie = `session_token=${data.session_token}; path=/; SameSite=Lax; Max-Age=${30 * 24 * 3600}`;
      }
      // Pass user data in navigate state so ProtectedRoute doesn't need
      // a round-trip /auth/me check (same pattern as AuthCallback)
      navigate('/admin/dashboard', {
        state: {
          user: {
            user_id: data.user_id,
            email: data.email,
            name: data.name,
            role: data.role,
            company_id: data.company_id,
            session_token: data.session_token
          }
        }
      });
    } else {
      setError('Login failed. Please try again.');
    }
    setGoogleLoading(false);
  };
  
  const markFormStart = () => {
    if (formStartedRef.current) return;
    formStartedRef.current = true;
    analytics.formStart('admin_login');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    analytics.formSubmit('admin_login');
    analytics.loginSubmit('admin');
    
    // Verify math captcha if present
    if (captchaRef.current && !captchaVerified) {
      const captchaInput = document.querySelector('[data-testid="captcha-answer-input"]');
      if (captchaInput && !captchaInput.value) {
        setError('Please solve the CAPTCHA');
        setLoading(false);
        return;
      }
      const expectedEl = document.querySelector('input[name="captcha_expected"]');
      const answerEl = document.querySelector('input[name="captcha_answer"]');
      if (expectedEl && answerEl) {
        if (parseInt(answerEl.value) !== parseInt(expectedEl.value)) {
          setError('Incorrect CAPTCHA answer. Please try again.');
          setLoading(false);
          return;
        }
      }
    }
    
    try {
      // Clear any leftover staff session so it doesn't override the new cookie
      localStorage.removeItem('staff_session_token');
      localStorage.removeItem('staff_type');
      delete axios.defaults.headers.common['Authorization'];

      const response = await axios.post(`${API}/auth/login`, {
        email,
        password,
        device_type: 1  // 1=browser, 2=mobile, 3=desktop
      }, {
        withCredentials: true
      });
      
      if (response.data.role === 'admin') {
        analytics.loginSuccess('admin');
        if (response.data.session_token) {
          document.cookie = `session_token=${response.data.session_token}; path=/; SameSite=Lax; Max-Age=${30 * 24 * 3600}`;
        }
        navigate('/admin/dashboard', {
          state: {
            user: {
              user_id: response.data.user_id,
              email: response.data.email,
              name: response.data.name,
              role: response.data.role,
              company_id: response.data.company_id,
              session_token: response.data.session_token
            }
          }
        });
      } else {
        setError('Access denied. Company Admin role required.');
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-100 p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            {branding.logo_url ? (
              <img 
                src={branding.logo_url} 
                alt={branding.platform_name} 
                className="h-16 object-contain"
              />
            ) : (
              <div className="p-4 bg-indigo-600 rounded-full">
                <Building2 className="w-12 h-12 text-white" />
              </div>
            )}
          </div>
          {/* Platform name hidden when logo is present */}
          {!branding.logo_url && (
            <h1 className="text-3xl font-bold text-gray-900">{branding.platform_name}</h1>
          )}
          <p className="text-gray-600 mt-2">{t('auth.admin_login')}</p>
        </div>
        
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>{t('nav.welcome')}</CardTitle>
                <CardDescription>{t('auth.admin_login')}</CardDescription>
              </div>
              <LanguageSelector compact />
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} onFocus={markFormStart} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t('auth.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="admin-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">{t('auth.password')}</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="admin-password-input"
                />
              </div>
              
              {/* CAPTCHA */}
              <div ref={captchaRef}>
                <Captcha 
                  loginType="admin"
                  onVerify={handleCaptchaVerify}
                  onError={handleCaptchaError}
                />
              </div>
              
              {error && (
                <div className="text-sm text-red-600 bg-red-50 p-3 rounded" data-testid="error-message">
                  {error}
                </div>
              )}
              <Button 
                type="submit" 
                className="w-full" 
                disabled={loading}
                data-testid="admin-login-button"
              >
                {loading ? t('common.loading') : t('auth.login_btn')}
              </Button>
            </form>
            
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-gray-500">Google</span>
              </div>
            </div>
            
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={handleGoogleLogin}
              disabled={googleLoading}
              data-testid="google-login-button"
            >
              {googleLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2" />
              ) : (
                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                </svg>
              )}
              {googleLoading ? t('common.loading') : 'Google'}
            </Button>
            
            <div className="mt-6 text-center text-sm text-gray-600">
              {t('auth.no_account')}{' '}
              <Link 
                to="/admin/signup" 
                className="text-indigo-600 hover:text-indigo-700 font-medium"
                data-testid="signup-link"
                onClick={() => analytics.signupStart()}
              >
                {t('auth.signup')}
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default CompanyAdminLogin;